import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getAiMlServicesSummary,
  getUnifiedAiMlEndpoints,
  getAimlEndpointFilters,
  getModelServingEndpointDetail,
  getModelServingEndpointTrend,
  getVectorSearchEndpointDetail,
  getVectorSearchEndpointTrend,
  getAiGatewayEndpointDetail,
  getAiGatewayEndpointTrend,
  getModelServingEndpointCallers,
  getAIFunctionUsageBreakdown,
} from "@/helpers/apis";
import type {
  AiMlServicesParams,
  UnifiedAIMLEndpointsParams,
  AimlEndpointFiltersParams,
  ModelServingEndpointDetailParams,
  VectorSearchEndpointDetailParams,
  AiGatewayEndpointDetailParams,
  EndpointCallersParams,
  AIFunctionUsageBreakdownParams,
} from "../aiml/types";

// AI/ML Services hooks
export const useAiMlServicesSummary = (params: AiMlServicesParams, enabled = true) =>
  useQuery({
    queryKey: ["aiml-services-summary", params],
    queryFn: () => getAiMlServicesSummary(params),
    enabled,
  });

export const useUnifiedAiMlEndpoints = (params: UnifiedAIMLEndpointsParams, enabled = true) =>
  useQuery({
    queryKey: ["unified-aiml-endpoints", params],
    queryFn: () => getUnifiedAiMlEndpoints(params),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useAimlEndpointFilters = (params: AimlEndpointFiltersParams, enabled = true) =>
  useQuery({
    queryKey: ["aiml-endpoint-filters", params],
    queryFn: () => getAimlEndpointFilters(params),
    enabled,
  });

export const useModelServingEndpointDetail = (
  params: ModelServingEndpointDetailParams,
  enabled = true
) =>
  useQuery({
    queryKey: [
      "model-serving-endpoint-detail",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getModelServingEndpointDetail(params),
    enabled,
  });

export const useModelServingEndpointTrend = (
  params: ModelServingEndpointDetailParams,
  enabled = true
) =>
  useQuery({
    queryKey: [
      "model-serving-endpoint-trend",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getModelServingEndpointTrend(params),
    enabled,
  });

export const useModelServingEndpointCallers = (params: EndpointCallersParams, enabled = true) =>
  useQuery({
    queryKey: [
      "model-serving-endpoint-callers",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getModelServingEndpointCallers(params),
    enabled,
  });

export const useAIFunctionUsageBreakdown = (
  params: AIFunctionUsageBreakdownParams,
  enabled = true
) =>
  useQuery({
    queryKey: [
      "ai-function-usage-breakdown",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getAIFunctionUsageBreakdown(params),
    enabled,
  });

export const useVectorSearchEndpointDetail = (
  params: VectorSearchEndpointDetailParams,
  enabled = true
) =>
  useQuery({
    queryKey: [
      "vector-search-endpoint-detail",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getVectorSearchEndpointDetail(params),
    enabled,
  });

export const useVectorSearchEndpointTrend = (
  params: VectorSearchEndpointDetailParams,
  enabled = true
) =>
  useQuery({
    queryKey: [
      "vector-search-endpoint-trend",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getVectorSearchEndpointTrend(params),
    enabled,
  });

export const useAiGatewayEndpointDetail = (params: AiGatewayEndpointDetailParams, enabled = true) =>
  useQuery({
    queryKey: [
      "ai-gateway-endpoint-detail",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getAiGatewayEndpointDetail(params),
    enabled,
  });

export const useAiGatewayEndpointTrend = (params: AiGatewayEndpointDetailParams, enabled = true) =>
  useQuery({
    queryKey: [
      "ai-gateway-endpoint-trend",
      params.endpoint_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getAiGatewayEndpointTrend(params),
    enabled,
  });
